import { Download, Edit, Eye, Mail, Printer, Trash2 } from 'lucide-react';
import type { RowActionItem, RowMenuEntry } from '../components/transactions/RowActionsMenu';
import type { PurchaseOrder } from '../types/purchase/purchaseOrder';

export interface PurchaseOrderRowActionHandlers {
  onOpen: () => void;
  onPrint: () => void;
  onDownloadPdf: () => void;
  onSend: () => void;
  onDelete: () => void;
}

export interface PurchaseOrderRowActionsLayout {
  inlineActions: RowActionItem[];
  menuEntries: RowMenuEntry[];
}

const canManagePurchaseOrders = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeletePurchaseOrders = () =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const buildActionCatalog = (
  handlers: PurchaseOrderRowActionHandlers
): Map<string, RowActionItem> => {
  const catalog = new Map<string, RowActionItem>();
  const canManage = canManagePurchaseOrders();

  catalog.set('edit', {
    id: 'edit',
    label: canManage ? 'Edit' : 'View',
    icon: canManage ? Edit : Eye,
    onClick: handlers.onOpen,
  });

  catalog.set('print', {
    id: 'print',
    label: 'Print',
    icon: Printer,
    onClick: handlers.onPrint,
  });

  catalog.set('download_pdf', {
    id: 'download_pdf',
    label: 'Download',
    icon: Download,
    onClick: handlers.onDownloadPdf,
  });

  if (canManage) {
    catalog.set('send', {
      id: 'send',
      label: 'Send email',
      icon: Mail,
      onClick: handlers.onSend,
    });
  }

  if (canDeletePurchaseOrders()) {
    catalog.set('delete', {
      id: 'delete',
      label: 'Delete',
      icon: Trash2,
      variant: 'destructive',
      onClick: handlers.onDelete,
    });
  }

  return catalog;
};

const getInlineActionIds = (catalog: Map<string, RowActionItem>): string[] => {
  if (catalog.has('edit')) return ['edit'];
  return [];
};

const MENU_GROUPS: string[][] = [['edit'], ['print', 'download_pdf', 'send'], ['delete']];

/** Primary actions inline; remaining actions grouped in overflow menu. */
export const buildPurchaseOrderRowActions = (
  _purchaseOrder: PurchaseOrder,
  handlers: PurchaseOrderRowActionHandlers
): PurchaseOrderRowActionsLayout => {
  const catalog = buildActionCatalog(handlers);
  const inlineIds = new Set(getInlineActionIds(catalog));

  const inlineActions = getInlineActionIds(catalog)
    .map((id) => catalog.get(id))
    .filter((item): item is RowActionItem => item != null);

  const menuEntries: RowMenuEntry[] = [];

  for (const group of MENU_GROUPS) {
    const groupItems = group
      .filter((id) => !inlineIds.has(id) && catalog.has(id))
      .map((id) => catalog.get(id)!);

    if (groupItems.length === 0) continue;
    if (menuEntries.length > 0) menuEntries.push({ type: 'separator' });
    menuEntries.push(...groupItems);
  }

  return { inlineActions, menuEntries };
};
